<script module lang="ts">
  import { _serviceWorker } from './+layout';
  import { stateGlobalClock } from '$states/globalClock.svelte';
  import { runGuards } from '../guards/utils/runGuards';
  import { afterNavigate } from '$app/navigation';
  import { browser } from '$app/environment';
  import { stateDelayedTransactionCountdowns } from '$states/delayedTransactionCountdowns.svelte';
  import { untrack } from 'svelte';

  if (browser && window.localStorage.getItem('darkTheme') === 'true') {
    document.documentElement.classList.add('dark');
  }

  _serviceWorker();

  if (browser) {
    stateGlobalClock.start();
  }
</script>

<script lang="ts">
  import '../app.css';
  import BottomSheet from '$components/bottom-sheet/BottomSheet.svelte';
  import { openModal } from '$components/bottom-sheet/index.svelte';
  import { onDestroy, onMount } from 'svelte';
  import ToastManager from '$components/toast-manager/ToastManager.svelte';
  import { beforeNavigate, goto, invalidateAll, onNavigate } from '$app/navigation';
  import { showToast } from '$components/toast-manager';
  import { ErrorType } from '$types/error';
  import QRScanner from '$components/qr-scanner/QRScanner.svelte';
  import ErrorModal from '$modals/error/ErrorModal.svelte';
  import PhotoCapture from '$components/photo-capture/PhotoCapture.svelte';
  import { isInAppBrowser } from '$utils/detectInAppBrowser';
  import InAppBrowser from '$modals/in-app-browser/InAppBrowser.svelte';
  import { stateNavigation } from '$states/navigation.svelte';
  import { stateError } from '$states/error.svelte';
  import { stateTransitionDirection } from '$lib/states/transitionDirection.svelte';
  import { updated } from '$app/state';
  import { stateIsOnline } from '$states/stateIsOnline.svelte';
  import Update from '$modals/Update.svelte';

  interface Props {
    children?: import('svelte').Snippet;
  }

  let { children }: Props = $props();

  /**
   * We're running guards here on client side, meaning we're likely to run into
   * a UI flash issue before guards manage to redirect.
   *
   * To prevent this, we hide content until guards run.
   */
  let isRunningGuards = $state(true);

  $effect(() => {
    if (stateError.current?.type === ErrorType.MODAL) {
      untrack(() => openModal(ErrorModal, { dismissable: false }));
    }
    if (stateError.current?.type === ErrorType.TOAST) {
      untrack(() => showToast('negative', stateError.current?.message ?? ''));
    }
  });

  $effect(() => {
    if (stateGlobalClock.inSeconds) {
      untrack(() => {
        stateDelayedTransactionCountdowns.cleanUpFromExpiredEntries();
      });
    }
  });

  let reRender = $state('portrait');

  onMount(async () => {
    if (isInAppBrowser()) {
      openModal(InAppBrowser);
    }

    window.onunhandledrejection = (event): void => {
      if (event.reason.name === 'NotAllowedError') {
        return;
      }

      stateError.set({
        type: ErrorType.SILENT,
        rawError: event.reason,
        message: `We're sorry, an unexpected error occurred: ${event.reason}`,
        actions: [
          {
            action: (): Promise<void> => goto('/migrate-domain'),
            text: 'Reload App',
          },
        ],
      });
    };
  });

  const landscapeQuery = window.matchMedia('(orientation: landscape)');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOrientationChange = (mediaQuery: any) => {
    if (!mediaQuery.matches) {
      reRender = 'landscape';
    } else {
      reRender = 'portrait';
    }
  };

  landscapeQuery.addEventListener('change', handleOrientationChange);

  onMount(() => {
    if (localStorage.getItem('updated') === 'true') {
      openModal(Update);
      localStorage.removeItem('updated');
    }
  });

  onDestroy(() => {
    landscapeQuery.removeEventListener('change', handleOrientationChange);
  });

  beforeNavigate((e) => {
    if (e.to?.url.pathname) {
      reportPathname(e.to!.url.pathname);
    }
  });

  onNavigate((e) => {
    let from = e.from?.url.pathname;
    let to = e.to?.url.pathname;

    if (!from || !to) {
      return;
    }

    if (to === from) {
      return;
    }

    if (e.from?.url.search) {
      from += e.from?.url.search;
    }

    if (e.to?.url.search) {
      to += e.to?.url.search;
    }

    if (to !== stateNavigation.last) {
      stateNavigation.current?.push(from);
    } else {
      stateNavigation.removeItem(to);
    }
  });

  const reportPathname = (pathname: string) => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(
        JSON.stringify({
          type: 'pathname',
          pathname: pathname,
        }),
      );
    }
  };

  const onUpdate = (): void => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations): void => {
        for (let registration of registrations) {
          registration.update();
        }
      });
    }

    localStorage.setItem('updated', 'true');
    window.location.reload();
  };

  $effect(() => {
    if (updated.current) {
      untrack(() => onUpdate());
    }
  });

  $effect(() => {
    const root = document.documentElement;

    if (!root) {
      return;
    }

    switch (stateTransitionDirection.current) {
      case 'left':
        root.classList.remove('transition-right');
        root.classList.add('transition-left');
        break;
      case 'right':
        root.classList.remove('transition-left');
        root.classList.add('transition-right');
        break;
      case null:
        root.classList.remove('transition-left');
        root.classList.remove('transition-right');
        break;
    }
  });

  let popstate = $state(false);

  const onPopstate = (): void => {
    popstate = true;
  };

  onMount(() => {
    window.addEventListener('popstate', onPopstate);
  });

  onDestroy(() => {
    window.removeEventListener('popstate', onPopstate);
  });

  const onOnline = (): void => {
    stateIsOnline.set(true);
    stateError.set(null);
    invalidateAll();
  };

  const onOffline = (): void => {
    stateIsOnline.set(false);
  };

  onMount(() => {
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
  });

  onDestroy(() => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  });

  onNavigate(async (navigation) => {
    if (navigation.from?.url.href === navigation.to?.url.href) {
      return;
    }

    if (!document.startViewTransition || popstate) {
      popstate = false;
      return;
    }

    return new Promise((resolve) => {
      document.startViewTransition(async () => {
        resolve();
        await navigation.complete;
      });
    });
  });

  /**
   * This runs and cancels navigation during client sessions:
   */
  beforeNavigate((event) => {
    runGuards(event);
  });

  /**
   * This is just for hard browser reloads (beforeNavigate does not run in such situation)
   */
  afterNavigate((event) => {
    runGuards(event).then((e) => {
      /**
       * Show content only when guards return null - meaning it's all ok.
       */
      if (e === null) {
        isRunningGuards = false;
      }
    });
  });

  onMount(() => {
    /**
     * Hot reloads don't trigger navigation events,
     * so we have to set this flag to false manually.
     * This is for dev only.
     *
     * https://github.com/sveltejs/kit/issues/4902
     */
    setTimeout(() => (isRunningGuards = false), 1000);
  });
</script>

<svelte:head></svelte:head>
{#key reRender}
  <div
    id="page"
    class="grid min-h-screen overflow-hidden bg-background"
    class:opacity-0={isRunningGuards}>
    {@render children?.()}
  </div>
{/key}

<BottomSheet></BottomSheet>
<ToastManager />
<QRScanner />
<PhotoCapture />
